import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import useAdminSummary from '../hooks/useAdminSummary';
import useOnboardingProgress from '../hooks/useOnboardingProgress';
import StatCard from '../components/StatCard';
import AssignTaskCard from '../components/AssignTaskCard';
import TasksYouCreatedCard from '../components/TasksYouCreatedCard';
import IntegrationHealthCard from '../components/IntegrationHealthCard';
import TaskAssignmentWizard from '../components/TaskAssignmentWizard';
import LoadingView from '../../shared/components/LoadingView';
import OnboardingProgressCard from '../components/OnboardingProgressCard';
import { OPEN_VIEW_PARAM, DASHBOARD_OPEN } from '../utils/openViewParams';

export default function AdminDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, loading, error } = useAdminSummary();
  const {
    completedCount,
    totalCount,
    isVisible: showOnboarding,
    dismissOnboarding,
  } = useOnboardingProgress(data);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [query, setQuery] = useState('');

  // Open sub-views when landing with ?open=<key> (e.g. from onboarding "Let's go")
  useEffect(() => {
    const open = searchParams.get(OPEN_VIEW_PARAM);
    if (open === DASHBOARD_OPEN.ASSIGN_TASK) {
      setWizardOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSearch = (q) => {
    setQuery(q);
    setWizardOpen(true);
  };

  if (loading) {
    return (
      <LoadingView
        symbolClass="bi bi-flow-chart"
        title="Loading dashboard"
        subtitle="Please wait while we prepare your workspace.."
      />
    );
  }
  if (error) return <div className="text-center text-danger mt-5">{error.message}</div>;

  return (
    // `mx-2` here fought the negative horizontal margins on every `.row` below and overflowed
    // the page by a few pixels. A container's padding exists to absorb those, which is half of
    // why AGENTS.md names `container py-4` as the page shell. The visible change is that the
    // dashboard is now width-capped and centred like the rest of the admin pages instead of
    // running to the edge of the window.
    <div className="container py-4">
      {/* ── Top stats row ───────────────────────── */}
      <div className="row">
        <StatCard
          title="Total Assets"
          value={data.totalAssets}
          changePct={data.assetsTrend}
          iconClass="bi bi-boombox-fill"
          classNameList="col-lg-3 col-md-6 mb-3"
        />
        {/*
          Maintella has no task priority concept, so the old `highPriorityTasks` was never
          populated and this rendered "undefined high priority". In-progress is real and useful:
          it separates work a field engineer has started from work still waiting.
        */}
        <StatCard
          title="Active Tasks"
          value={data.activeTasks}
          subValue={`${data.taskStatusCounts.ongoing} in progress`}
          iconClass="bi bi-list-check"
          classNameList="col-lg-3 col-md-6 mb-3"
        />
        {showOnboarding && (
          <OnboardingProgressCard
            completedCount={completedCount}
            totalCount={totalCount}
            onDismiss={dismissOnboarding}
            className="col-lg-3 col-md-6 mb-3"
          />
        )}
      </div>

      {/*
        Two explicit columns rather than three col-lg-6 in one row. With a single row, a long
        task list made the row as tall as itself and wrapped anything after it far down the
        page, leaving dead space alongside. Separate columns stack independently, so the compact
        cards sit together on the left and the list is free to grow on the right.
      */}
      <div className="row mb-3">
        <div className="col-lg-6 col-md-12">
          <AssignTaskCard
            colSize="mb-3"
            onSearch={handleSearch}
            initialAssetSearch={query}
          />
          {/* Admin-only, and renders nothing when the organization has no active integrations. */}
          <IntegrationHealthCard colSize="mb-3" />
        </div>
        <div className="col-lg-6 col-md-12">
          <TasksYouCreatedCard colSize="mb-3" />
        </div>
        <TaskAssignmentWizard
          show={wizardOpen}
          initialQuery={query}
          onClose={() => setWizardOpen(false)}
        />
      </div>
    </div>
  );
}